import { Box, FlexBox, Typography, getTokenCssVar } from '@vp/swan';
import { __ } from '@99designs/i18n';
import { FolderPlusIcon, FolderXIcon } from './icons';
import { REFERENCE_PROJECTS_DECISION_WITHOUT } from './parseReply';
import type { ReferenceProjectsReply } from './parseReply';

type ReferenceProjectsMessageViewProps = {
    data: ReferenceProjectsReply;
};

/**
 * The settled, read-only answer for a reference-projects turn, shown inside the customer's own
 * message bubble.
 *
 * Shows the decision and, for an opt-in, how many projects were referenced — deliberately not
 * thumbnails. The pilot's code comments promise thumbnails but its message view never rendered
 * them, so adding them here would be a new feature rather than parity, and would need the project
 * previews re-fetched for every historical message on load.
 */
export function ReferenceProjectsMessageView({ data }: ReferenceProjectsMessageViewProps) {
    if (data.decision === REFERENCE_PROJECTS_DECISION_WITHOUT) {
        return (
            <ConfirmationCard>
                <FlexBox alignItems="center" gap={3} paddingY={4} paddingX={5}>
                    <FolderXIcon />
                    <Typography fontSkin="body-standard-bold">
                        {__('No, continue without', {
                            _context:
                                'Option declining to reference any past projects in the chat brief',
                        })}
                    </Typography>
                </FlexBox>
            </ConfirmationCard>
        );
    }

    return (
        <ConfirmationCard>
            <FlexBox flexDirection="column" gap={3} paddingY={4} paddingX={5}>
                <FlexBox alignItems="center" gap={3}>
                    <FolderPlusIcon />
                    <Typography fontSkin="body-standard-bold">
                        {__('Yes, select projects', {
                            _context:
                                'Option choosing to reference past projects in the chat brief',
                        })}
                    </Typography>
                </FlexBox>
                {data.workIds.length > 0 && (
                    <Typography fontSkin="body-small">
                        {data.workIds.length === 1
                            ? __('1 project selected', {
                                  _context:
                                      'Confirmation in the chat thread of how many past projects the customer referenced; singular form',
                              })
                            : __('{{count}} projects selected', {
                                  count: data.workIds.length,
                                  _context:
                                      'Confirmation in the chat thread of how many past projects the customer referenced; plural form; count is a number',
                              })}
                    </Typography>
                )}
            </FlexBox>
        </ConfirmationCard>
    );
}

/**
 * No border, for the same reason ChatInterface's composer band has none: the pilot asks for
 * `1px solid var(--swan-sem-color-border-subtle)`, but SWAN never defines that custom property,
 * so the declaration is invalid and dropped and no border has ever rendered in production.
 */
function ConfirmationCard({ children }: { children: React.ReactNode }) {
    return (
        <Box
            backgroundColor="standard"
            style={{
                borderRadius: getTokenCssVar('SwanSemBorderRadiusStandard'),
                overflow: 'hidden',
            }}
        >
            {children}
        </Box>
    );
}
